import { API_CONFIG } from './config';

export class APIError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string,
    public details?: any
  ) {
    super(message);
    this.name = 'APIError';
  }
}

interface CacheEntry {
  data: any;
  timestamp: number;
  ttl: number;
}

class SimpleCache {
  private cache = new Map<string, CacheEntry>();

  set(key: string, data: any, ttl: number = 5 * 60 * 1000) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  get(key: string): any | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const isExpired = Date.now() - entry.timestamp > entry.ttl;
    if (isExpired) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  clear() {
    this.cache.clear();
  }

  delete(pattern: string) {
    // Delete entries matching pattern
    const keys = Array.from(this.cache.keys());
    for (const key of keys) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    }
  }
}

class APIClient {
  private baseURL: string;
  private headers: Record<string, string>;
  private cache = new SimpleCache();
  private authToken: string | null = null;

  constructor() {
    this.baseURL = API_CONFIG.baseURL;
    this.headers = { ...API_CONFIG.headers };
  }

  setAuthToken(token: string | null) {
    this.authToken = token;
    if (token) {
      this.headers['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.headers['Authorization'];
    }
  }

  private async sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async retryRequest(
    fn: () => Promise<Response>,
    attempts: number = API_CONFIG.retryAttempts
  ): Promise<Response> {
    for (let i = 0; i < attempts; i++) {
      try {
        const response = await fn();
        
        // Don't retry on client errors (4xx)
        if (response.status >= 400 && response.status < 500) {
          return response;
        }
        
        // Retry on server errors (5xx) or network errors
        if (!response.ok && i < attempts - 1) {
          await this.sleep(API_CONFIG.retryDelay * (i + 1));
          continue;
        }
        
        return response;
      } catch (error) {
        if (i === attempts - 1) throw error;
        await this.sleep(API_CONFIG.retryDelay * (i + 1));
      }
    }
    
    throw new Error('Max retry attempts reached');
  }

  private getCacheKey(url: string, options?: RequestInit): string {
    const method = options?.method || 'GET';
    const body = options?.body ? JSON.stringify(options.body) : '';
    return `${method}:${url}:${body}`;
  }

  async request<T = any>(
    endpoint: string,
    options: RequestInit & { 
      skipCache?: boolean;
      cacheTTL?: number;
    } = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const { skipCache, cacheTTL, ...fetchOptions } = options;
    
    // Check cache for GET requests
    if (!skipCache && (!fetchOptions.method || fetchOptions.method === 'GET')) {
      const cacheKey = this.getCacheKey(url, fetchOptions);
      const cachedData = this.cache.get(cacheKey);
      if (cachedData) {
        return cachedData;
      }
    }

    const requestOptions: RequestInit = {
      ...fetchOptions,
      headers: {
        ...this.headers,
        ...(fetchOptions.headers || {}),
      },
      credentials: API_CONFIG.credentials,
    };

    try {
      const response = await this.retryRequest(() => 
        fetch(url, requestOptions)
      );

      // Handle 401 Unauthorized
      if (response.status === 401) {
        // Clear auth token and cache
        this.setAuthToken(null);
        this.cache.clear();
        throw new APIError('Unauthorized', 401, 'UNAUTHORIZED');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new APIError(
          data.error || `Request failed with status ${response.status}`,
          response.status,
          data.code,
          data.details
        );
      }

      // Cache successful GET requests
      if (!skipCache && (!fetchOptions.method || fetchOptions.method === 'GET')) {
        const cacheKey = this.getCacheKey(url, fetchOptions);
        this.cache.set(cacheKey, data, cacheTTL);
      }

      return data;
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }
      
      if (error instanceof Error) {
        throw new APIError(
          error.message || 'Network error',
          undefined,
          'NETWORK_ERROR'
        );
      }
      
      throw new APIError('Unknown error occurred');
    }
  }

  // Convenience methods
  async get<T = any>(endpoint: string, options?: RequestInit & { skipCache?: boolean; cacheTTL?: number }): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put<T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete<T = any>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  // File upload
  async upload<T = any>(endpoint: string, formData: FormData, options?: RequestInit): Promise<T> {
    // Create headers without Content-Type to let browser set it with boundary
    const uploadHeaders: Record<string, string> = {};
    
    // Copy headers except Content-Type
    Object.entries(this.headers).forEach(([key, value]) => {
      if (key.toLowerCase() !== 'content-type') {
        uploadHeaders[key] = value;
      }
    });
    
    // Add any additional headers from options
    if (options?.headers) {
      const optHeaders = options.headers as Record<string, string>;
      Object.entries(optHeaders).forEach(([key, value]) => {
        if (key.toLowerCase() !== 'content-type') {
          uploadHeaders[key] = value;
        }
      });
    }
    
    const requestOptions: RequestInit = {
      ...options,
      method: 'POST',
      body: formData,
      headers: uploadHeaders,
      credentials: API_CONFIG.credentials,
    };

    const url = `${this.baseURL}${endpoint}`;
    const response = await this.retryRequest(() => fetch(url, requestOptions));

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new APIError(
        data.error || `Upload failed with status ${response.status}`,
        response.status,
        data.code,
        data.details
      );
    }

    return response.json();
  }

  // Clear cache
  clearCache(pattern?: string) {
    if (pattern) {
      this.cache.delete(pattern);
    } else {
      this.cache.clear();
    }
  }
}

// Export singleton instance
export const apiClient = new APIClient();